import React, { useState, useEffect } from 'react';
import ReportTable, { Column } from '../ReportTable';
import { MaintenanceRequest, MaintenanceContract } from '../../types';
import { api } from '../../services/api';

interface MaintenanceReportsProps {
  maintenance: MaintenanceRequest[];
  initialReport?: string;
}

const MaintenanceReports: React.FC<MaintenanceReportsProps> = ({ maintenance, initialReport }) => {
  const [selectedReport, setSelectedReport] = useState<string>(initialReport || 'requests');
  const [statusFilter, setStatusFilter] = useState<string>('All');
  const [companyFilter, setCompanyFilter] = useState<string>('All');
  const [maintenanceContracts, setMaintenanceContracts] = useState<MaintenanceContract[]>([]);

  useEffect(() => {
    if (initialReport) {
      setSelectedReport(initialReport);
    }
    api.maintenance.contracts.getAll().then(data => {
        setMaintenanceContracts(data);
    }).catch(err => console.error("Error fetching maintenance contracts", err));
  }, [initialReport]);

  const filteredMaintenance = maintenance.filter(m => {
      const statusMatch = statusFilter === 'All' || m.status === statusFilter;
      const companyMatch = companyFilter === 'All' || m.companyName === companyFilter;
      return statusMatch && companyMatch;
  });

  const requestsData = filteredMaintenance.map(m => ({
      property: m.propertyName,
      unit: m.unitNumber || 'عام',
      company: m.companyName || 'فريق داخلي',
      desc: m.description,
      status: m.status === 'Completed' ? 'مكتمل' : m.status === 'In Progress' ? 'جاري' : 'معلق',
      date: new Date(m.dateReported).toLocaleDateString('ar-KW'),
      cost: m.finalCost || 0
  }));

  const requestsColumns: Column[] = [
      { header: 'المقاول', accessor: 'company' },
      { header: 'العقار', accessor: 'property' },
      { header: 'الوحدة', accessor: 'unit' },
      { header: 'تاريخ التبليغ', accessor: 'date' },
      { header: 'وصف العطل', accessor: 'desc' },
      { header: 'التكلفة', accessor: 'cost', render: val => `${Number(val).toLocaleString()} د.ك` },
      { header: 'الحالة', accessor: 'status', render: val => (
          <span className={`px-2 py-1 rounded-full text-[10px] font-bold ${
              val === 'مكتمل' ? 'bg-emerald-50 text-emerald-600' : val === 'جاري' ? 'bg-blue-50 text-blue-600' : 'bg-rose-50 text-rose-600'
          }`}>{val}</span>
      )}
  ];

  const performanceData = filteredMaintenance.filter(m => m.status === 'Completed').map(m => {
      const reported = new Date(m.dateReported);
      const completed = m.completionDate ? new Date(m.completionDate) : new Date(reported.getTime() + 3 * 24 * 60 * 60 * 1000);
      const diffTime = Math.abs(completed.getTime() - reported.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      return {
          company: m.companyName || 'فريق داخلي',
          desc: m.description,
          reported: reported.toLocaleDateString('ar-KW'),
          completed: completed.toLocaleDateString('ar-KW'),
          days: diffDays
      };
  });

  const performanceColumns: Column[] = [
      { header: 'المقاول / الشركة', accessor: 'company' },
      { header: 'وصف العطل', accessor: 'desc' },
      { header: 'تاريخ البلاغ', accessor: 'reported' },
      { header: 'تاريخ الإغلاق', accessor: 'completed' },
      { header: 'وقت الإنجاز', accessor: 'days', render: val => (
          <div className="flex items-center gap-2">
              <span className={`font-bold ${Number(val) <= 2 ? 'text-emerald-600' : Number(val) <= 5 ? 'text-orange-500' : 'text-rose-600'}`}>{val} يوم</span>
          </div>
      )}
  ];

  const companiesSummary = filteredMaintenance.reduce((acc, curr) => {
      const company = curr.companyName || 'فريق داخلي';
      if (!acc[company]) {
          acc[company] = { company, totalCost: 0, orderCount: 0, completed: 0 };
      }
      acc[company].totalCost += Number(curr.finalCost || 0);
      acc[company].orderCount += 1;
      if (curr.status === 'Completed') acc[company].completed += 1;
      return acc;
  }, {} as Record<string, any>);

  const companiesData = Object.values(companiesSummary);

  const companiesColumns: Column[] = [
      { header: 'المقاول / الشركة', accessor: 'company' },
      { header: 'إجمالي الطلبات', accessor: 'orderCount' },
      { header: 'الطلبات المكتملة', accessor: 'completed' },
      { header: 'إجمالي المصاريف', accessor: 'totalCost', render: val => <span className="font-bold text-slate-800">{Number(val).toLocaleString()} د.ك</span> }
  ];

  const contractsData = maintenanceContracts.map(c => ({
      company: c.companyName || 'غير محدد',
      property: c.propertyName || 'عقد شامل',
      start: c.startDate ? new Date(c.startDate).toLocaleDateString('ar-KW') : '-',
      end: c.endDate ? new Date(c.endDate).toLocaleDateString('ar-KW') : '-',
      value: c.contractValue,
      terms: c.paymentTerms === 'Monthly' ? 'شهري' : c.paymentTerms === 'Yearly' ? 'سنوي' : c.paymentTerms === 'Quarterly' ? 'ربع سنوي' : 'دفعة واحدة',
      status: c.status === 'Active' ? 'ساري' : c.status === 'Expired' ? 'منتهي' : 'ملغي'
  }));

  const contractsColumns: Column[] = [
      { header: 'الشركة المقاولة', accessor: 'company' },
      { header: 'العقار المشمول', accessor: 'property' },
      { header: 'بداية العقد', accessor: 'start' },
      { header: 'نهاية العقد', accessor: 'end' },
      { header: 'الدفعة', accessor: 'terms' },
      { header: 'قيمة العقد', accessor: 'value', render: val => `${Number(val).toLocaleString()} د.ك` },
      { header: 'الحالة', accessor: 'status', render: val => (
          <span className={`px-2 py-1 rounded-full text-[10px] font-bold ${
              val === 'ساري' ? 'bg-emerald-50 text-emerald-600' : val === 'منتهي' ? 'bg-rose-50 text-rose-600' : 'bg-slate-100 text-slate-500'
          }`}>{val}</span>
      )}
  ];

  const customFilters = (
    <div className="flex items-center gap-3">
        <select 
          className="border-slate-200 rounded-xl p-2 bg-white text-xs font-bold shadow-sm"
          value={companyFilter} 
          onChange={(e) => setCompanyFilter(e.target.value)}
        >
          <option value="All">جميع المقاولين</option>
          {Array.from(new Set(maintenance.map(m => m.companyName).filter(Boolean))).map(c => (
              <option key={c} value={c}>{c}</option>
          ))}
        </select>

        <select 
          className="border-slate-200 rounded-xl p-2 bg-white text-xs font-bold shadow-sm"
          value={statusFilter} 
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="All">جميع الحالات</option>
          <option value="Pending">معلق</option>
          <option value="In Progress">جاري</option>
          <option value="Completed">مكتمل</option>
        </select>
    </div>
  );

  return (
    <div className="flex flex-col h-full space-y-6">
      <div className="px-6 py-4 bg-white rounded-3xl border border-slate-200 shadow-sm flex items-center justify-between">
         <div className="flex items-center gap-4">
            <span className="text-sm font-black text-slate-400">تحليل الصيانة:</span>
            <div className="flex bg-slate-100 p-1 rounded-2xl">
               {(['requests', 'performance', 'companies', 'contracts'] as const).map(tab => (
                  <button
                    key={tab}
                    onClick={() => setSelectedReport(tab)}
                    className={`px-6 py-2 rounded-xl text-xs font-black transition-all ${
                        selectedReport === tab ? 'bg-white text-fly-600 shadow-md' : 'text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    {tab === 'requests' ? 'سجل الطلبات' : tab === 'performance' ? 'سرعة الإنجاز' : tab === 'companies' ? 'تقييم الشركات' : 'عقود الصيانة'}
                  </button>
               ))}
            </div>
         </div>
      </div>

      <div className="flex-1">
          {selectedReport === 'requests' && <ReportTable title="سجل تذاكر الصيانة" columns={requestsColumns} data={requestsData} customFilters={customFilters} />}
          {selectedReport === 'performance' && <ReportTable title="تحليل سرعة إنجاز الاعطال" columns={performanceColumns} data={performanceData} customFilters={customFilters} />}
          {selectedReport === 'companies' && <ReportTable title="إحصائيات تكاليف المقاولين" columns={companiesColumns} data={companiesData} customFilters={customFilters} />}
          {selectedReport === 'contracts' && <ReportTable title="سجل عقود الصيانة" columns={contractsColumns} data={contractsData} />}
      </div>
    </div>
  );
};

export default MaintenanceReports;
